import React, {useMemo, useState} from 'react';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {debounce} from '../../../domain/lib/debounce';
import settingsParams from '../../../components/cart/settingsParams';
import {useControllerProps} from '../../../domain/controllers/ControllerContext';
import {TextArea, TextButton, TextButtonPriority} from 'wix-ui-tpa/cssVars';
import s from './BuyerNote.scss';
import {classes} from './BuyerNote.st.css';
import {useSettingsWithDefaults} from '../../../hooks/useSettingsWithDefaults';
import {Document} from '@wix/wix-ui-icons-common';
import type {ISettingsParam} from '@wix/tpa-settings';

export enum BuyerNoteDataHook {
  Root = 'BuyerNoteDataHook.root',
  Button = 'BuyerNoteDataHook.button',
  Body = 'BuyerNoteDataHook.body',
}

export interface BuyerNoteProps {
  buttonCaptionSettingKey?: ISettingsParam;
  buttonPlaceholderSettingKey?: ISettingsParam;
}

const AddNoteButton = ({handleClick, caption, isOpen}: {handleClick: () => void; caption: string; isOpen: boolean}) => {
  const {sideCartNote} = useControllerProps().cartStore;

  return (
    <TextButton
      data-hook={BuyerNoteDataHook.Button}
      priority={TextButtonPriority.primary}
      className={sideCartNote ? classes.textButton : classes.textButtonOld}
      prefixIcon={<Document />}
      aria-expanded={isOpen}
      onClick={handleClick}>
      {caption}
    </TextButton>
  );
};

const AddNoteBody = ({
  placeholder,
  handleChange,
  buyerNote,
}: {
  placeholder: string;
  handleChange: any;
  buyerNote: string;
}) => {
  const {t} = useTranslation();
  const {sideCartNote} = useControllerProps().cartStore;

  return (
    <div className={s.body}>
      <TextArea
        className={sideCartNote ? classes.textArea : classes.textAreaOld}
        data-hook={BuyerNoteDataHook.Body}
        placeholder={placeholder}
        autoFocus={true}
        maxLength={250}
        value={buyerNote || ''}
        onChange={handleChange}
        ariaLabel={t('cart.buyer_note_placeholder')}
      />
    </div>
  );
};

export const BuyerNote = ({buttonCaptionSettingKey, buttonPlaceholderSettingKey}: BuyerNoteProps) => {
  const {getSettingsWithDefaults} = useSettingsWithDefaults();
  const {updateBuyerNote, sendAddNoteBi, sideCartNote} = useControllerProps().cartStore;
  const [buyerNote, setBuyerNote] = useState<string>(useControllerProps().cartStore.cartModel.buyerNote);
  const [isOpen, setIsOpen] = useState<boolean>(Boolean(buyerNote));
  const debouncedUpdateBuyerNote = useMemo(() => debounce(updateBuyerNote), [updateBuyerNote]);

  const handleChange = ({target: {value}}: React.ChangeEvent<HTMLTextAreaElement>) => {
    setBuyerNote(value);
    return debouncedUpdateBuyerNote(value);
  };

  const handleOpen = () => {
    !isOpen && sendAddNoteBi();
    setIsOpen(!isOpen);
  };

  return (
    <div className={s.note} data-hook={BuyerNoteDataHook.Root}>
      <AddNoteButton
        isOpen={isOpen}
        handleClick={handleOpen}
        caption={
          sideCartNote
            ? getSettingsWithDefaults(buttonCaptionSettingKey)
            : getSettingsWithDefaults(settingsParams.CART_ADD_NOTE_LINK)
        }
      />
      {isOpen && (
        <AddNoteBody
          placeholder={
            sideCartNote
              ? getSettingsWithDefaults(buttonPlaceholderSettingKey)
              : getSettingsWithDefaults(settingsParams.CART_INSTRUCTIONS_TEXT)
          }
          handleChange={handleChange}
          buyerNote={buyerNote}
        />
      )}
    </div>
  );
};
